<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ChevronDown, CircleUserRound, Download, Home, LayoutDashboard, LogOut, Menu, MoonStar, Settings2, ShieldCheck, Type, Volume2, VolumeX, X } from '@lucide/vue'
import { standaloneMode } from '../api'
import { openFontLibrary } from '../font'
import { music, openMusicLibrary } from '../music'
import { auth, hasCreatorAccess, logout, preferences, toggleMotion } from '../state'
import brandLogo from '../assets/brand/logo-lumiscale-final.svg'
import ContextNavigation from './ContextNavigation.vue'

const open = ref(false)
const experienceMenu = ref<HTMLDetailsElement | null>(null)
const route = useRoute()
const router = useRouter()
const onlineLocalCreator = computed(() => !standaloneMode && route.path.startsWith('/local-create'))

watch(() => route.fullPath, () => {
  open.value = false
  if (experienceMenu.value) experienceMenu.value.open = false
})

async function signOut() {
  await logout()
  open.value = false
  await router.push('/')
}

function openSoundSettings() {
  open.value = false
  if (experienceMenu.value) experienceMenu.value.open = false
  openMusicLibrary()
}

function openFontSettings() {
  open.value = false
  if (experienceMenu.value) experienceMenu.value.open = false
  openFontLibrary()
}

function toggleMotionSetting() {
  toggleMotion()
}
</script>

<template>
  <div class="global-navigation">
    <header class="site-header">
      <RouterLink class="brand" to="/" aria-label="心光量域首页" @click="open = false">
        <img class="brand-logo" :src="brandLogo" alt="" width="52" height="42">
        <span class="brand-copy"><strong>心光量域</strong><small>LUMISCALE</small></span>
      </RouterLink>
      <span v-if="standaloneMode || onlineLocalCreator" class="standalone-pill">{{ standaloneMode ? '直接打开版 · 数据仅存本机' : '在线本地创作 · 不上传服务器' }}</span>

      <button class="mobile-menu icon-button" :aria-label="open ? '关闭导航' : '打开导航'" :title="open ? '关闭导航' : '打开导航'" :aria-expanded="open" aria-controls="global-main-nav" @click="open = !open"><X v-if="open" /><Menu v-else /></button>

      <nav id="global-main-nav" :class="['main-nav', { open }]" aria-label="全站主导航">
        <RouterLink to="/" @click="open = false"><Home :size="17" />首页</RouterLink>
        <RouterLink to="/explore" @click="open = false">心光馆 · 量域馆</RouterLink>
        <RouterLink v-if="!standaloneMode" to="/local-create" @click="open = false"><Download :size="17" />{{ onlineLocalCreator ? '本地创作台' : '本地创作' }}</RouterLink>
        <RouterLink v-if="hasCreatorAccess()" to="/creator" @click="open = false"><LayoutDashboard :size="17" />{{ standaloneMode ? '本地创作台' : '内容工作台' }}</RouterLink>
        <RouterLink v-if="['admin', 'safety_admin', 'super_admin'].includes(auth.user?.role || '')" to="/admin" @click="open = false"><ShieldCheck :size="17" />站长审核台</RouterLink>
        <div class="nav-spacer" />
        <details ref="experienceMenu" class="experience-menu">
          <summary title="字体、声音与动效设置"><Settings2 :size="18" /><span>体验设置</span><ChevronDown class="experience-chevron" /></summary>
          <div class="experience-menu-panel">
            <button type="button" @click="openFontSettings"><Type :size="18" /><span><b>字体衣橱</b><small>切换平台字体或使用自己的字体</small></span></button>
            <button type="button" @click="openSoundSettings">
              <Volume2 v-if="music.playing || preferences.sound" :size="18" /><VolumeX v-else :size="18" />
              <span><b>声音与音乐</b><small>{{ music.playing ? '私人音乐正在播放' : preferences.sound ? '答题音效已开启' : '当前保持安静' }}</small></span>
            </button>
            <button type="button" @click="toggleMotionSetting"><MoonStar :size="18" /><span><b>柔和动效</b><small>{{ preferences.reducedMotion ? '已减少动态效果' : '保留轻柔过渡效果' }}</small></span></button>
          </div>
        </details>
        <template v-if="auth.user && !standaloneMode">
          <RouterLink class="user-chip" to="/account" @click="open = false"><CircleUserRound :size="18" />{{ auth.user.name }}</RouterLink>
          <button class="nav-action" title="退出当前账号" aria-label="退出当前账号" @click="signOut"><LogOut :size="18" /><span>退出</span></button>
        </template>
        <RouterLink v-else-if="!standaloneMode" class="button button-small" to="/login" @click="open = false">站长 / 管理员入口</RouterLink>
      </nav>
    </header>
    <ContextNavigation />
  </div>
</template>
